import {
    AlertModal,
    Box,
    Button,
    FooterBanner,
    Modal,
    SearchSelect,
    showToast,
    TextBlock,
} from "@bcc-ui/ui-components";
import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { AssetCustomView } from "domain/asset-management/assets";
import { assetViewService } from "services/asset-management/AssetCustomViewService";
import { StoreState } from "store";
import { logger } from "utils/logging";

interface Props {
    isOpen: boolean;
    onSuccess?: () => void;
    onClose: () => void;
    selectedAssetCount?: number;
    selectedAssetIds: string[];
    currentViewUuid?: string;
}

export const MoveAssetsToGroupModal = (props: Props) => {
    const { t } = useTranslation();
    const storeViews = useSelector((state: StoreState) => state.assetViewsReducer.views);

    const [selectedGroupUuid, setSelectedGroupUuid] = React.useState("");
    const [isLoading, setIsLoading] = React.useState(false);
    const [hasErrors, setHasErrors] = React.useState(false);
    const [isErrorModalOpen, setIsErrorModalOpen] = React.useState(false);
    const collectGroups = (views: AssetCustomView[]): AssetCustomView[] =>
        views.flatMap((view) => (view.folder ? collectGroups(view.childrenViews) : [view]));

    const handleAdd = async () => {
        if (!selectedGroupUuid) {
            setHasErrors(true);
            return;
        }
        setIsLoading(true);
        try {
            await assetViewService.addAssetsToGroup(selectedGroupUuid, props.selectedAssetIds);
            showToast(t("AssetManagement.createAssetCustomView.successMessage"), "success");
            props.onClose();
            props.onSuccess?.();
            window.location.replace("/assets/views/" + selectedGroupUuid);
        } catch (error) {
            logger.error("Failed to add assets to group:", error);
            setIsErrorModalOpen(true);
        } finally {
            setIsLoading(false);
        }
    };

    const groupItems = [
        { value: "", label: t("AssetManagement.moveAssetsToGroup.selectGroup") },
        ...collectGroups(storeViews ?? [])
            .filter((view) => view.uuid !== props.currentViewUuid)
            .map((view) => ({ value: view.uuid, label: view.name })),
    ];

    return (
        <>
            <Modal
                title={t("AssetManagement.moveAssetsToGroup.title")}
                isOpen={props.isOpen}
                hideModal={props.onClose}
                buttons={
                    <Box gap="Md">
                        <Button variant="Primary" onClick={handleAdd} disabled={hasErrors || isLoading}>
                            {t("AssetManagement.moveAssetsToGroup.add")}
                        </Button>
                        <Button variant="Secondary" onClick={props.onClose}>
                            {t("Common.cancel")}
                        </Button>
                    </Box>
                }
                footerBanner={hasErrors && <FooterBanner text={t("Common.fixErrors")} />}
            >
                <Box columnLayout>
                    <TextBlock>
                        {t("AssetManagement.moveAssetsToGroup.assetsSelected", { count: props.selectedAssetCount })}
                    </TextBlock>
                    <SearchSelect
                        name="groupSelect"
                        label={t("AssetManagement.moveAssetsToGroup.selectGroup")}
                        placeholder={t("AssetManagement.moveAssetsToGroup.selectGroup")}
                        selectedByDefault={selectedGroupUuid}
                        items={groupItems}
                        onSelection={(value) => {
                            setSelectedGroupUuid(String(value));
                            setHasErrors(false);
                        }}
                        closeOnSelection={true}
                        dropdownMaxHeight={250}
                        error={hasErrors ? t("AssetManagement.moveAssetsToGroup.selectGroup") : undefined}
                    />
                </Box>
            </Modal>
            <AlertModal
                title={t("Common.failure")}
                variant={"Error"}
                isOpen={isErrorModalOpen}
                hideModal={() => setIsErrorModalOpen(false)}
                buttons={
                    <Button onClick={() => setIsErrorModalOpen(false)} variant={"Primary"}>
                        {t("Common.ok")}
                    </Button>
                }
            >
                <TextBlock>{t("AssetManagement.moveAssetsToGroup.failureMessage")}</TextBlock>
            </AlertModal>
        </>
    );
};
